import HealthService from '../../services/HealthService'
import AxiosErrorHandler from '../../helpers/AxiosErrorHandler'
import router from '../../router'

export const namespaced = true

const RESOURCE_METRICS = ['cpu', 'memory', 'swap', 'disk']
const SYNTHETIC_TARGETS = ['masterplan', 'building']

const initialState = {
  statuses: {},
  incidents: [],
  history: [],
  resourceHistory: {},
  syntheticHistory: {},
  requestLogs: [],
  errorLogs: [],
  alertRecipients: [],
  emailsSuppressed: false
}

export const state = {
  statuses: {},
  incidents: [],
  history: [],
  resourceHistory: {},
  syntheticHistory: {},
  requestLogs: [],
  errorLogs: [],
  alertRecipients: [],
  emailsSuppressed: false
}

export const mutations = {
  SET_STATUSES(state, data) {
    state.statuses = data || {}
  },
  SET_INCIDENTS(state, data) {
    state.incidents = data || []
  },
  SET_HISTORY(state, data) {
    state.history = data || []
  },
  SET_RESOURCE_HISTORY(state, data) {
    state.resourceHistory = data || {}
  },
  SET_SYNTHETIC_HISTORY(state, data) {
    state.syntheticHistory = data || {}
  },
  SET_REQUEST_LOGS(state, data) {
    state.requestLogs = data || []
  },
  SET_ERROR_LOGS(state, data) {
    state.errorLogs = data || []
  },
  SET_ALERT_RECIPIENTS(state, data) {
    state.alertRecipients = data || []
  },
  SET_EMAILS_SUPPRESSED(state, data) {
    state.emailsSuppressed = data === true
  },
  RESET_STORE(state) {
    Object.assign(state, initialState)
  }
}

export const actions = {
  fetchStatus: function(context) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    return HealthService.getStatus().then(
      r => {
        const payload = r.data.message || {}
        context.commit('SET_STATUSES', payload.statuses)
        context.commit('SET_INCIDENTS', payload.incidents)
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      },
      e => {
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
        AxiosErrorHandler.handler(e, context, router)
      }
    )
  },
  fetchHistory: function(context, { target, limit } = {}) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    return HealthService.getHistory(target, limit).then(
      r => {
        context.commit('SET_HISTORY', r.data.message)
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      },
      e => {
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
        AxiosErrorHandler.handler(e, context, router)
      }
    )
  },
  runCheck: async function(context) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    try {
      await HealthService.runCheck()
      await Promise.all([
        context.dispatch('fetchStatus'),
        context.dispatch('fetchSyntheticHistory')
      ])
    } catch (e) {
      context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      AxiosErrorHandler.handler(e, context, router)
    }
  },
  restartProcess: async function(context, processName) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    try {
      await HealthService.restartProcess(processName)
      await context.dispatch('fetchStatus')
    } catch (e) {
      context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      AxiosErrorHandler.handler(e, context, router)
    }
  },
  fetchResourceHistory: async function(context, limit = 50) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    try {
      const results = await Promise.all(
        RESOURCE_METRICS.map(metric =>
          HealthService.getHistory('resource:' + metric, limit)
        )
      )
      const data = {}
      RESOURCE_METRICS.forEach((metric, i) => {
        data[metric] = results[i].data.message || []
      })
      context.commit('SET_RESOURCE_HISTORY', data)
      context.commit('SET_LOADING_STATUS', 'idle', { root: true })
    } catch (e) {
      context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      AxiosErrorHandler.handler(e, context, router)
    }
  },
  fetchSyntheticHistory: async function(context, limit = 20) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    try {
      const results = await Promise.all(
        SYNTHETIC_TARGETS.map(name =>
          HealthService.getHistory('synthetic:' + name, limit)
        )
      )
      const data = {}
      SYNTHETIC_TARGETS.forEach((name, i) => {
        data[name] = results[i].data.message || []
      })
      context.commit('SET_SYNTHETIC_HISTORY', data)
      context.commit('SET_LOADING_STATUS', 'idle', { root: true })
    } catch (e) {
      context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      AxiosErrorHandler.handler(e, context, router)
    }
  },
  fetchRequestLogs: function(context, filters = {}) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    return HealthService.getRequestLogs(filters).then(
      r => {
        context.commit('SET_REQUEST_LOGS', r.data.message)
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      },
      e => {
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
        AxiosErrorHandler.handler(e, context, router)
      }
    )
  },
  fetchErrorLogs: function(context, filters = {}) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    return HealthService.getErrorLogs(filters).then(
      r => {
        context.commit('SET_ERROR_LOGS', r.data.message)
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      },
      e => {
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
        AxiosErrorHandler.handler(e, context, router)
      }
    )
  },
  fetchAlertRecipients: function(context) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    return HealthService.getAlertRecipients().then(
      r => {
        context.commit('SET_ALERT_RECIPIENTS', r.data.message)
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      },
      e => {
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
        AxiosErrorHandler.handler(e, context, router)
      }
    )
  },
  saveAlertRecipients: async function(context, emails) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    try {
      const r = await HealthService.saveAlertRecipients(emails)
      context.commit('SET_ALERT_RECIPIENTS', r.data.message)
      context.dispatch(
        'NotificationStore/add',
        { type: 'info', message: 'Alert recipients saved' },
        { root: true }
      )
      context.commit('SET_LOADING_STATUS', 'idle', { root: true })
    } catch (e) {
      context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      AxiosErrorHandler.handler(e, context, router)
    }
  },
  fetchEmailsSuppressed: function(context) {
    return HealthService.getEmailsSuppressed().then(
      r => {
        context.commit('SET_EMAILS_SUPPRESSED', r.data.message)
      },
      e => {
        AxiosErrorHandler.handler(e, context, router)
      }
    )
  },
  saveEmailsSuppressed: async function(context, suppressed) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    try {
      const r = await HealthService.saveEmailsSuppressed(suppressed)
      context.commit('SET_EMAILS_SUPPRESSED', r.data.message)
      context.dispatch(
        'NotificationStore/add',
        {
          type: 'info',
          message: r.data.message
            ? 'Alert emails are now suppressed — nothing will be sent until this is turned off'
            : 'Alert emails re-enabled'
        },
        { root: true }
      )
      context.commit('SET_LOADING_STATUS', 'idle', { root: true })
    } catch (e) {
      context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      AxiosErrorHandler.handler(e, context, router)
    }
  },
  resetStore: async function(context) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    context.commit('RESET_STORE')
    context.commit('SET_LOADING_STATUS', 'idle', { root: true })
  }
}

export const getters = {
  statusList: state => {
    return Object.values(state.statuses || {})
  }
}
